import { NameSpaces, useCrossTabEventsListener } from 'modules/EventsManager';
import { client } from 'modules/Apollo/client';
import { logger } from 'modules/logger';

/**
 * Listens for user settings patch events from other tabs and updates the local Apollo cache.
 * This ensures all tabs stay in sync when user settings like docking state change.
 * Supports patching any fields under the `me` query (e.g., worklistSettings, theme, etc).
 */
export const usePatchUserSettings = () => {
  useCrossTabEventsListener(NameSpaces.USER_SETTINGS_PATCH, ({ payload }) => {
    if (payload.type !== 'userSettingsPatch') {
      return;
    }

    const { patch, id } = payload?.data || {};

    logger.info('[usePatchUserSettings] Event received', { patch });
    if (!id || !patch || typeof patch !== 'object') {
      logger.warn('[usePatchUserSettings] Invalid user settings patch event received', {
        payload,
      });
      return;
    }

    // Build field functions similar to usePatchWorklistItem
    const fields: Record<string, (existing: unknown) => unknown> = {};

    for (const [k, v] of Object.entries(patch)) {
      if (k === '__typename') continue;
      // For nested objects, merge with existing values; for primitives, replace
      fields[k] = (existing) => {
        if (
          typeof v === 'object' &&
          v !== null &&
          typeof existing === 'object' &&
          existing !== null
        ) {
          return { ...existing, ...v };
        }
        return v;
      };
    }

    try {
      const meId = client.cache.identify({ __typename: 'User', id });

      if (meId) {
        client.cache.modify({
          id: meId,
          fields,
        });
        logger.info('[usePatchUserSettings] Cache modified successfully', { meId, fields });
      } else {
        logger.warn('[usePatchUserSettings] No me object found in cache');
      }

      logger.info('[usePatchUserSettings] User settings patched in cache', { patch });
    } catch (error) {
      logger.error('[usePatchUserSettings] Failed to patch user settings', { error, patch });
    }
  });
};
